import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import {
  FloodCategoryLevel,
  FloodImpact,
} from 'features/waterLevel/waterLevel.types';
import { useTranslation } from 'react-i18next';
import CurrentLevelPlaceMark from 'assets/water-level/current-level-place-mark.svg?react';
import { formatWaterLevelValue } from 'features/waterLevel/waterLevel.utils';
import {
  getLevelIndicatorItems,
  LevelIndicatorItem,
  MARKER_FULL_BLEED_WIDTH,
} from './FloodImpacts.utils';

type FloodImpactsProps = {
  floodImpacts: FloodImpact[];
  floodCategories: FloodCategoryLevel[];
  currentLevel?: number;
  levelUnits: string;
  forecastPeakLevel?: number;
};

type LevelBadgeProps = {
  level: number;
  backgroundColor: string;
  useContrastText: boolean;
  levelUnits: string;
  sx?: SxProps<Theme>;
  isCurrentLevel?: boolean;
};

const LevelBadge = (props: LevelBadgeProps): JSX.Element => {
  const {
    level,
    backgroundColor,
    useContrastText,
    levelUnits,
    sx = {},
    isCurrentLevel = false,
  } = props;

  const chip = (
    <Chip
      size="small"
      variant="watchduty-basic"
      label={`${formatWaterLevelValue(level)} ${levelUnits}`}
      sx={[
        (theme: Theme) => ({
          backgroundColor,
          color: useContrastText
            ? theme.palette.getContrastText(backgroundColor)
            : 'text.primary',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );

  if (!isCurrentLevel) return chip;

  return (
    <Stack sx={{ position: 'relative' }}>
      <CurrentLevelPlaceMark
        style={{ position: 'absolute', top: 0, left: -24 }}
      />
      {chip}
    </Stack>
  );
};

type LevelIndicatorRowProps = {
  item: LevelIndicatorItem;
  levelUnits: string;
  isFirstRow?: boolean;
};

const badgeSx = {
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
} as const;

const LevelIndicatorRow = (props: LevelIndicatorRowProps): JSX.Element => {
  const { item, levelUnits, isFirstRow } = props;

  const isStandaloneMarker = Boolean(item.isMarker);
  const isMergedImpact = Boolean(item.markerSlot);
  const showThresholdLine =
    Boolean(item.isThreshold) && !isStandaloneMarker && !isMergedImpact;
  const isCurrentLevel = Boolean(item.hasCurrentLevelMarker);

  return (
    <ListItem sx={{ pl: 4, py: 0 }}>
      <ListItemText
        primary={
          isMergedImpact ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ width: MARKER_FULL_BLEED_WIDTH }}
            >
              <LevelBadge
                level={item.level}
                backgroundColor={item.backgroundColor}
                useContrastText={item.useContrastText}
                levelUnits={levelUnits}
                sx={badgeSx}
                isCurrentLevel={isCurrentLevel}
              />
              {item.markerSlot}
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              sx={
                showThresholdLine
                  ? { width: MARKER_FULL_BLEED_WIDTH }
                  : undefined
              }
            >
              <LevelBadge
                level={item.level}
                backgroundColor={item.backgroundColor}
                useContrastText={item.useContrastText}
                levelUnits={levelUnits}
                sx={isStandaloneMarker ? badgeSx : undefined}
                isCurrentLevel={isCurrentLevel}
              />
              {showThresholdLine ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    alignSelf: 'center',
                    height: 2,
                    backgroundColor: item.backgroundColor,
                  }}
                />
              ) : null}
            </Stack>
          )
        }
        secondary={item.body}
        sx={{
          display: 'flex',
          flexDirection: isStandaloneMarker ? 'row' : 'column',
          gap: isStandaloneMarker ? 0 : 1,
          pt: showThresholdLine && !isFirstRow ? 2.5 : undefined,
        }}
        slotProps={{
          primary: { component: 'div' },
          secondary: {
            component: 'div',
            sx: { flexGrow: 1 },
          },
        }}
      />
    </ListItem>
  );
};

export const FloodImpacts = (props: FloodImpactsProps): JSX.Element => {
  const {
    floodImpacts,
    floodCategories,
    currentLevel,
    levelUnits,
    forecastPeakLevel,
  } = props;
  const { t } = useTranslation();

  const items = getLevelIndicatorItems({
    floodImpacts,
    floodCategories,
    currentLevel,
    forecastPeakLevel,
    t,
  });

  return (
    <List
      subheader={
        <ListSubheader
          id="flood-impacts-title"
          disableSticky
          sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            lineHeight: 1,
            p: 2,
            pt: 1,
            color: 'inherit',
            mb: 2,
          }}
        >
          <Typography variant="h3">{t('waterLevel.floodImpacts')}</Typography>
        </ListSubheader>
      }
      aria-labelledby="flood-impacts-title"
      dense
    >
      {items.map((item, index) => (
        <LevelIndicatorRow
          key={item.key}
          item={item}
          levelUnits={levelUnits}
          isFirstRow={index === 0}
        />
      ))}
    </List>
  );
};
